import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    InputBase,
    IconButton,
    Paper,
    Fade,
    Button,
    Menu,
    MenuItem,
    Divider,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    Avatar,
    ListItemText, ListItemButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MicIcon from '@mui/icons-material/Mic';
import ClearIcon from '@mui/icons-material/Clear';
import CategoryIcon from '@mui/icons-material/Category';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { suggestProducts } from '../api/Api';
import { useLocation, useNavigate } from 'react-router-dom';

const DEFAULT_IMAGE_PATH = "/img/img.png";

const getImageUrl = (imagePath: string | null | undefined) => {
    if (!imagePath) return (process.env.PUBLIC_URL || '') + DEFAULT_IMAGE_PATH;
    return `https://tikrai.com/api/images/${imagePath}`;
};

const SearchComponent = ({ onSearch, isMobile, categories, onCategorySelect }) => {
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categoryAnchorEl, setCategoryAnchorEl] = useState(null);

    // Suggest state
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const abortRef = useRef(null);

    const inputRef = useRef(null);

    // Sync selected category with URL path (keeps selection after navigation)
    useEffect(() => {
        const segments = location.pathname.split('/').filter(Boolean);
        // Use the last path segment to match data-loading logic (useCategoryData)
        const currentAlias = segments.length > 0 ? segments[segments.length - 1] : '';
        setSelectedCategory(currentAlias);
    }, [location.pathname]);

    // Clear search when returning to the main page
    useEffect(() => {
        if (location.pathname === '/') {
            setSearchQuery('');
            setSuggestions([]);
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        }
    }, [location.pathname]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            onSearch(searchQuery.trim(), selectedCategory);
            setShowSuggestions(false);
        }
    };

    const handleClear = () => {
        setSearchQuery('');
        setSuggestions([]);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        inputRef.current?.focus();
    };

    // Debounced suggest fetch
    useEffect(() => {
        const term = searchQuery.trim();
        if (!isFocused || term.length === 0) {
            setSuggestions([]);
            setShowSuggestions(false);
            setHighlightedIndex(-1);
            if (abortRef.current) abortRef.current.abort();
            return;
        }

        const controller = new AbortController();
        abortRef.current = controller;
        const t = setTimeout(async () => {
            const items = await suggestProducts(term, 5, controller.signal);
            setSuggestions(items);
            setShowSuggestions(true);
            setHighlightedIndex(-1);
        }, 250);
        return () => {
            clearTimeout(t);
            controller.abort();
        };
    }, [searchQuery, isFocused]);

    const selectSuggestion = (item) => {
        if (!item) return;
        setSearchQuery(item.name || '');
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        // Optional: trigger search immediately
        onSearch((item.name || '').trim(), selectedCategory);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter') {
            if (highlightedIndex >= 0) {
                e.preventDefault();
                selectSuggestion(suggestions[highlightedIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch(e);
        }
    };

    const handleCategoryClick = (category) => {
        setSelectedCategory(category.alias);
        if (onCategorySelect) {
            onCategorySelect(category.alias);
        }
        setCategoryAnchorEl(null);
    };

    const prettifyAlias = (alias) => {
        if (!alias) return 'All Categories';
        try {
            alias = decodeURIComponent(alias);
        } catch {}
        const words = alias.replace(/[-_]+/g, ' ').trim().split(/\s+/);
        return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    const selectedCategoryName = selectedCategory
        ? (categories.find(cat => cat.alias === selectedCategory)?.name || prettifyAlias(selectedCategory))
        : 'All Categories';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                marginBottom: { xs: 3, sm: 4 },
                paddingX: { xs: 1, sm: 2 },
            }}
        >
            {/* Combined Search Field with Categories */}
            <Box sx={{ position: 'relative', width: '100%', maxWidth: { xs: '100%' } }}>
            <Paper
                component="form"
                onSubmit={handleSearch}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: { xs: '100%' },
                    height: { xs: 50, sm: 56 },
                    borderRadius: { xs: 25, sm: 28 },
                    backgroundColor: 'white',
                    boxShadow: isFocused
                        ? '0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(66,133,244,0.3)'
                        : '0 2px 5px rgba(0,0,0,0.1)',
                    border: isFocused ? '1px solid #4285f4' : '1px solid #e0e0e0',
                    transition: 'all 0.2s ease-in-out',
                    overflow: 'hidden',
                    '&:hover': {
                        boxShadow: isFocused
                            ? '0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(66,133,244,0.3)'
                            : '0 2px 8px rgba(0,0,0,0.12)',
                    },
                }}
            >
                {/* Categories Dropdown Button */}
                <Button
                    onClick={(e) => setCategoryAnchorEl(e.currentTarget)}
                    sx={{
                        minWidth: { xs: 150, sm: 180 },
                        height: '100%',
                        borderRadius: 0,
                        textTransform: 'none',
                        color: '#5f6368',
                        backgroundColor: 'transparent',
                        paddingX: { xs: 1, sm: 2 },
                        justifyContent: 'space-between',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                    }}
                    endIcon={<ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        maxWidth: { xs: 60, sm: 120 }
                    }}>
                        <CategoryIcon sx={{
                            fontSize: { xs: 16, sm: 18 },
                            marginRight: 0.5
                        }} />
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {selectedCategoryName}
                        </Typography>
                    </Box>
                </Button>

                {/* Vertical Divider */}
                <Divider orientation="vertical" flexItem sx={{ backgroundColor: '#e0e0e0' }} />

                {/* Search Input */}
                <InputBase
                    ref={inputRef}
                    sx={{
                        flex: 1,
                        fontSize: { xs: 16, sm: 18 },
                        color: '#202124',
                        paddingX: { xs: 1, sm: 2 },
                        '& .MuiInputBase-input': {
                            padding: 0,
                            '&::placeholder': {
                                color: '#9aa0a6',
                                opacity: 1,
                            },
                        },
                    }}
                    placeholder="Search for products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setTimeout(() => setIsFocused(false), 150)}
                    onKeyPress={handleKeyPress}
                    onKeyDown={handleKeyDown}
                />

                {/* Clear Button */}
                <Fade in={searchQuery.length > 0}>
                    <IconButton
                        onClick={handleClear}
                        sx={{
                            padding: { xs: 1, sm: 1.5 },
                            color: '#9aa0a6',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.04)',
                            },
                        }}
                        aria-label="clear"
                    >
                        <ClearIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </IconButton>
                </Fade>

                {/* Microphone Icon */}
                <IconButton
                    sx={{
                        padding: { xs: 1, sm: 1.5 },
                        color: '#9aa0a6',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                    }}
                    aria-label="voice search"
                >
                    <MicIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>


                {/* Search Button */}
                <IconButton
                    type="submit"
                    sx={{
                        padding: { xs: 1, sm: 1.5 },
                        marginRight: { xs: 0.5, sm: 1 },
                        backgroundColor: '#2e7d32',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: '#1b5e20',
                        },
                    }}
                    aria-label="search"
                >
                    <SearchIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </Paper>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute',
                        top: { xs: 52, sm: 58 },
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        borderRadius: 2,
                        overflow: 'hidden',
                        maxHeight: 320,
                    }}
                    onMouseDown={(e) => e.preventDefault()} // keep focus while clicking
                >
                    <List dense disablePadding>
                        {suggestions.map((item, idx) => (
                            <ListItemButton
                                key={item.id || item.alias || item.name || idx}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectSuggestion(item)}
                                selected={idx === highlightedIndex}
                                sx={{
                                    cursor: 'pointer',
                                    backgroundColor: idx === highlightedIndex ? 'rgba(25,118,210,0.08)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' },
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar
                                        src={item.image ? `${getImageUrl(item.image)}.png` : (process.env.PUBLIC_URL || '') + DEFAULT_IMAGE_PATH}
                                        alt={item.name || ''}
                                        imgProps={{
                                            loading: 'lazy',
                                            onError: (e: any) => { e.currentTarget.src = (process.env.PUBLIC_URL || '') + DEFAULT_IMAGE_PATH; }
                                        }}
                                        sx={{ width: 40, height: 40, border: '1px solid #eee' }}
                                    >
                                        {(item.name || '?').charAt(0)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={item.name}
                                    secondary={item.lastPrice ? `€ ${item.lastPrice}` : undefined}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>
            )}
            </Box>

            {/* Categories Dropdown Menu */}
            <Menu
                anchorEl={categoryAnchorEl}
                open={Boolean(categoryAnchorEl)}
                onClose={() => setCategoryAnchorEl(null)}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                <MenuItem
                    onClick={() => handleCategoryClick({ alias: '', name: 'All Categories' })}
                    selected={selectedCategory === ''}
                    sx={{
                        paddingY: 1.5,
                        paddingX: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(66, 165, 245, 0.08)',
                        }
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            flexShrink: 0
                        }}
                    >
                        <CategoryIcon sx={{ fontSize: 20, color: '#666' }} />
                    </Box>
                    <Typography variant="body1" sx={{ flex: 1, minWidth: 200 }}>All Categories</Typography>
                </MenuItem>
                <Divider />
                {categories.map((category) => (
                    <MenuItem
                        key={category.alias}
                        onClick={() => handleCategoryClick(category)}
                        selected={selectedCategory === category.alias}
                        sx={{
                            paddingY: 1.5,
                            paddingX: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(66, 165, 245, 0.08)',
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 1,
                                flexShrink: 0,
                                overflow: 'hidden'
                            }}
                        >
                            {category.iconUrl ? (
                                <img
                                    src={category.iconUrl}
                                    alt={category.name}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                <CategoryIcon sx={{ fontSize: 20, color: '#666' }} />
                            )}
                        </Box>
                        <Typography variant="body1" sx={{ flex: 1 }}>{category.name}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default SearchComponent;